import { SYNTHETIC_CATEGORY_IDS, deepestCategoryId, pathIdsTo } from './category-tree';
import type { CategoryFolderPhase, CategoryNode, RoomDocument } from '../api/types';

/**
 * The folder tree in the checklist spreadsheet's shape (owner ask 2026-10-15):
 * Phase › Category › Sub-category › …, in the spreadsheet's order rather than
 * A–Z. The categories endpoint carries the room checklist's phases and order
 * (`phases`, `categoryOrder` — src/modules/dd/folder-order.ts); these helpers
 * only re-arrange the tree the page already has. DISPLAY ONLY: no tag, parent,
 * filing or grant changes, and a phase is never a category id (it is never sent
 * to the search as a tag filter).
 */

/** The final group: top-level categories no phase holds (AI-made, custom,
 * "Uncategorized"). */
export const OTHER_PHASE_KEY = 'other';
export const OTHER_PHASE_NAME = 'Other';

/** One phase row in the folder tree: its top-level categories and their count. */
export interface FolderPhaseGroup {
  /** The phase id, or {@link OTHER_PHASE_KEY}. */
  key: string;
  name: string;
  /** Documents under the phase: the sum of its categories' counts (each
   * document is counted once, under one top-level category). */
  documentCount: number;
  nodes: CategoryNode[];
}

/**
 * The tree with every level's children in checklist order: a category the
 * checklist reaches first, in its order; the rest (accounts and institutions,
 * states, categories the AI or an admin added) after them by name, and
 * "Uncategorized" last. Year / month levels are not categories and are
 * untouched. Without a checklist order the tree is returned as it came.
 */
export function orderFolderTree(
  nodes: readonly CategoryNode[],
  categoryOrder: readonly string[] | undefined,
): CategoryNode[] {
  if (!categoryOrder || categoryOrder.length === 0) return [...nodes];
  const rank = new Map(categoryOrder.map((id, i) => [id, i] as const));
  const sortLevel = (list: readonly CategoryNode[]): CategoryNode[] =>
    [...list]
      .sort((a, b) => {
        const ra = rank.get(a.id);
        const rb = rank.get(b.id);
        if (ra !== undefined && rb !== undefined) return ra - rb;
        if (ra !== undefined) return -1;
        if (rb !== undefined) return 1;
        const ua = SYNTHETIC_CATEGORY_IDS.has(a.id);
        if (ua !== SYNTHETIC_CATEGORY_IDS.has(b.id)) return ua ? 1 : -1;
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
      })
      .map((node) => ({ ...node, children: sortLevel(node.children) }));
  return sortLevel(nodes);
}

/**
 * The top-level categories grouped under their phases, in phase order, then a
 * final "Other" for the rest. Null when the room has no checklist phases — the
 * tree then starts at the categories, as before.
 */
export function folderPhaseGroups(
  nodes: readonly CategoryNode[],
  phases: readonly CategoryFolderPhase[] | undefined,
): FolderPhaseGroup[] | null {
  if (!phases || phases.length === 0) return null;
  const byId = new Map(nodes.map((n) => [n.id, n] as const));
  const taken = new Set<string>();
  const groups: FolderPhaseGroup[] = [];
  for (const phase of [...phases].sort((a, b) => a.position - b.position)) {
    const members: CategoryNode[] = [];
    for (const id of phase.categoryIds) {
      const node = byId.get(id);
      if (!node || taken.has(id)) continue;
      taken.add(id);
      members.push(node);
    }
    if (members.length > 0) groups.push(group(phase.id, phase.name, members));
  }
  if (groups.length === 0) return null;
  const rest = nodes.filter((n) => !taken.has(n.id));
  if (rest.length > 0) groups.push(group(OTHER_PHASE_KEY, OTHER_PHASE_NAME, rest));
  return groups;
}

function group(key: string, name: string, nodes: CategoryNode[]): FolderPhaseGroup {
  return { key, name, nodes, documentCount: nodes.reduce((n, c) => n + c.documentCount, 0) };
}

/**
 * Whether a document is under a phase: its folder (the deepest of its
 * categories in the tree — where the counts put it) sits under one of the
 * phase's top-level categories. A document with no folder belongs to "Other",
 * beside "Uncategorized".
 */
export function documentInPhase(
  doc: Pick<RoomDocument, 'categoryTagIds'>,
  phase: FolderPhaseGroup,
  parentOf: ReadonlyMap<string, string | null>,
): boolean {
  const folder = deepestCategoryId(doc.categoryTagIds, parentOf);
  if (folder === null) return phase.key === OTHER_PHASE_KEY;
  const root = pathIdsTo(folder, parentOf)[0];
  return phase.nodes.some((n) => n.id === root);
}

/** The folder tree as the data room shows it: every level in checklist order,
 * and the phases above the categories (null when the room has none). */
export function folderPhaseView(
  tree: readonly CategoryNode[],
  order: { phases?: CategoryFolderPhase[]; categoryOrder?: string[] } | undefined,
): { nodes: CategoryNode[]; phases: FolderPhaseGroup[] | null } {
  const nodes = orderFolderTree(tree, order?.categoryOrder);
  return { nodes, phases: folderPhaseGroups(nodes, order?.phases) };
}
